
import { Component, Vue, Prop } from "vue-property-decorator";
import {
  getMatchShellPackageInfo,
  purchaseOrderBatchMatch,
  queryPoShellPackage,
  savePurchaseOrderShellPackages
} from "@/api/shell-package";
import {deepClone} from "@/utils/deepClone";

@Component({
  name: "ModifyDiscounts",
})
export default class extends Vue {
  @Prop({ default: false }) private show: boolean;
  @Prop( { default: "" }) private proformaInvoiceId;
  @Prop({ default: "" }) private piNumber: string;
  @Prop({ default: "" }) private customerAbbr: string;

  private tableData = [];
  private tableLoading = false;
  private saving = false;
  private saved = false;
  private confirmVisible = false;
  private confirmSelectAll = false;
  private confirmTitle = "";
  private confirmMessage = "";
  private detailVisible = false;
  private detailLoading = false;
  private detailTitle = "";
  private detailList = [];

  private close() {
    this.confirmVisible = false;
    this.detailVisible = false;
    const saved = this.saved;
    this.saved = false;
    this.$emit("update:show", false);
    if (saved) {
      this.$emit("success");
    }
  }

  private visibleShells(row) {
    return (row.shellPackages || []).filter(item => item.shellPackage);
  }

  private async openShellDetail(row, shell) {
    this.detailTitle = shell.shellPackage;
    this.detailList = [];
    this.detailVisible = true;
    this.detailLoading = true;
    try {
      const res = await queryPoShellPackage({
        purchaseOrderId: row.purchaseOrderId,
        shellPackageId: shell.shellPackageId,
      }) as any;
      this.detailList = (res || []).filter(item => {
        const description = (item.description || "").trim();
        const value = (item.value || "").trim();
        return description || value;
      });
    } catch (error) {
      console.log(error);
    } finally {
      this.detailLoading = false;
    }
  }

  private toSelectOptions(packages: any[] = []) {
    return (packages || [])
        .map(item => ({
          label: item.shellPackage != null ? item.shellPackage : item.label,
          value: item.shellPackageId != null ? item.shellPackageId : item.value,
        }))
        .filter(item => item.value != null && item.value !== "")
        .sort((a, b) => String(a.label).localeCompare(String(b.label), "zh"));
  }

  private optionIds(row) {
    return (row.shellPackageOption || []).map(option => option.value);
  }

  private shellsFromIds(row, ids) {
    const optionMap = new Map(
        (row.shellPackageOption || []).map(option => [option.value, option.label])
    );
    return (ids || [])
        .filter(id => optionMap.has(id))
        .map(id => ({
          shellPackage: optionMap.get(id),
          shellPackageId: id,
        }));
  }

  private applyMatchedOptions(row, matchedPackages) {
    const options = this.toSelectOptions(matchedPackages);
    const currentNames = (row.shellPackages || [])
        .map(item => (item.shellPackage || "").trim())
        .filter(name => name);
    row.shellPackageOption = options;
    row.shellPackageIds = currentNames.length === 0
        ? options.map(option => option.value)
        : options
            .filter(option => currentNames.includes(String(option.label || "").trim()))
            .map(option => option.value);
  }

  private async open() {
    this.tableLoading = true;
    this.saved = false;
    this.tableData = [];
    try {
      const list = await getMatchShellPackageInfo(this.proformaInvoiceId) as any;
      this.tableData = (list || []).map(item => ({
        ...item,
        shellPackageOption: [],
        shellPackageIds: [],
      }));
      await this.fillMatchedOptions();
    } catch (error) {
      console.log(error);
    } finally {
      this.tableLoading = false;
    }
  }

  private async fillMatchedOptions() {
    if (!this.tableData.length) return;
    const purchaseOrderIds = this.tableData.map(item => item.purchaseOrderId);
    const res = await purchaseOrderBatchMatch({purchaseOrderIds}) as any;
    const matchedList = res || [];
    this.tableData.forEach(item => {
      const matched = matchedList.find(val => val.purchaseOrderId === item.purchaseOrderId);
      this.applyMatchedOptions(item, matched ? matched.shellPackages : []);
    });
    this.tableData = deepClone(this.tableData);
  }

  private openConfirm(selectAll: boolean) {
    if (!this.tableData.length) {
      this.$message.warning("暂无采购订单");
      return;
    }
    this.confirmSelectAll = selectAll;
    this.confirmTitle = "全部更新";
    this.confirmMessage = selectAll
        ? "确定要把所有采购订单的【现壳体信息】替换为最新的壳体信息吗（包含全部华强品名）？"
        : "确定要把所有采购订单的【现壳体信息】替换为【品牌壳体信息】吗？";
    this.confirmVisible = true;
  }

  private confirmSubmit() {
    this.submit(this.tableData, this.confirmSelectAll);
  }

  private async submit(rows, selectAll: boolean) {
    if (this.saving) return;
    this.saving = true;
    const data = {
      purchaseOrderShellPackageReqs: rows.map(item => ({
        purchaseOrderId: item.purchaseOrderId,
        shellPackageInfoIds: selectAll ? this.optionIds(item) : (item.shellPackageIds || []),
      })),
    };
    try {
      await savePurchaseOrderShellPackages(data);
      rows.forEach((row, index) => {
        const ids = data.purchaseOrderShellPackageReqs[index].shellPackageInfoIds;
        if (selectAll) {
          this.$set(row, "shellPackageIds", ids);
        }
        this.$set(row, "shellPackages", this.shellsFromIds(row, ids));
      });
      this.saved = true;
      this.confirmVisible = false;
      this.$notify({
        title: "成功",
        message: "操作成功",
        type: "success",
        duration: 2000
      });
    } catch (error) {
      console.log(error);
    } finally {
      this.saving = false;
    }
  }
}
